import React, {useCallback, useState} from "react";
import {useTranslation} from "react-i18next";
import {Box, DialogActions, DialogContent, DialogTitle, Divider} from "@mui/material";
import Dialog from "../../base/dialog";
import Button from "../../base/button/button";
import Form, {MuiRFF, Yup} from "../../form";
import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import useIsMounted from "../../../hooks/use-is-mounted";
import Api from "../../../../core/services/api/api";
import {ReactComponent as CheckboxIcon} from "../../../../assets/images/check-box/checkbox.svg";
import {ReactComponent as ActiveCheckboxIcon} from "../../../../assets/images/check-box/checkbox-active.svg";

const formNames = {
    title: 'title',
    route: 'route',
    isPublic: 'isPublic',
};

const schema = Yup.object().shape({
    [formNames.title]: Yup.string().trim().nullable().required(ValidateMessages.required),
    [formNames.route]: Yup.string().trim().nullable().required(ValidateMessages.required).test(
        'no-spaces',
        ValidateMessages.incorrectType("URL Route. So no spaces between the characters are allowed."),
        value => !/\s/.test(value || '')
    ),
    [formNames.isPublic]: Yup.boolean().required(ValidateMessages.required),
});

const validate = MuiRFF.makeValidate(schema);
const required = MuiRFF.makeRequired(schema);

const initialValues = {
    [formNames.title]: '',
    [formNames.route]: '',
    [formNames.isPublic]: false,
};

const CreateDynamicPageDialog = ({
                                     open,
                                     close,
                                     onSuccess,
                                 }) => {
    const {t} = useTranslation('dialogs', {keyPrefix: 'create-dynamic-page'});
    const [submitting, setSubmitting] = useState(false);
    const isMounted = useIsMounted();

    /**
     *  Handles the form submission to create a new dynamic page. It prepares the data, calls the API, and manages the submission state.
     * @type {(function(*): Promise<void>)|*}
     */
    const createDynamicPage = useCallback(async (values) => {
        const forApi = {
            title: values[formNames.title]?.trim(),
            route: values[formNames.route]?.trim(),
            isPublic: values[formNames.isPublic] ?? false,
        };
        setSubmitting(true);
        const response = await Api.createDynamicPage({
            name: forApi.route,
            title: forApi.title,
            pageContent: '',
            isPublic: forApi.isPublic ?? false,
            isPopup: false,
        });
        if (!isMounted())
            return;
        setSubmitting(false);
        if (response?.resultFlag && response.data) {
            onSuccess?.({
                id: response.data.id,
                title: response.data.title ?? '',
                route: response.data.name ?? '',
                isPublic: response.data.isPublic ?? false,
            });
            close();
        }
    }, [close, isMounted, onSuccess]);

    return (
        <Dialog open={open} setOpen={close} maxWidth={'sm'}>
            <DialogTitle>
                {t('title')}
            </DialogTitle>
            <DialogContent dividers>
                <Form
                    onSubmit={createDynamicPage}
                    validate={validate}
                    initialValues={initialValues}
                    formProps={{id: 'create-dynamic-page-form'}}
                >
                    {({values}) => (
                        <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, pt: 1}}>
                            <MuiRFF.TextField
                                name={formNames.title}
                                label={t('titleLabel')}
                                placeholder={t('titlePlaceholder')}
                                required={required[formNames.title]}
                            />
                            <MuiRFF.TextField
                                name={formNames.route}
                                label={t('routeLabel')}
                                placeholder={t('routePlaceholder')}
                                required={required[formNames.route]}
                            />
                            <MuiRFF.Checkboxes
                                name={formNames.isPublic}
                                color={'primary'}
                                icon={<CheckboxIcon/>}
                                checkedIcon={<ActiveCheckboxIcon/>}
                                data={{
                                    value: values[formNames.isPublic],
                                    label: t('isPublicLabel'),
                                }}
                                required={required[formNames.isPublic]}
                                formControlLabelProps={{
                                    sx: {marginBlockEnd: 0, marginInlineEnd: 0,},
                                }}
                            />
                        </Box>
                    )}
                </Form>
            </DialogContent>
            <Divider/>
            <DialogActions sx={{p: 0, m: 0, justifyContent: 'space-between'}}>
                <Button
                    variant={'dialog'}
                    borderRadius={'0 8px 0 8px'}
                    color={'primary'}
                    onClick={close}
                    disabled={submitting}
                >
                    {t('close')}
                </Button>
                <Button
                    variant={'dialog'}
                    color={'primary'}
                    type={'submit'}
                    form={'create-dynamic-page-form'}
                    disabled={submitting}
                    borderRadius={'8px 0 8px 0'}
                >
                    {submitting ? t('submitting') : t('submit')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CreateDynamicPageDialog;